import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import React from "react";
import {
    Chip,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
    Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CustomImage from "../../components/CustomImage";
import SearchInput from "../../components/SearchInput";
import { isHRUser, uploadFile } from "../../utils/helper";
import { downloadCsvFile } from "../../utils/export";
import { MODULES } from "../../utils/constants";
import { useAppContext } from "../../services/context/AppContext";
import { useCorporateStore } from "../../services/corporates";
import { fetchCorporateEventUsers, uploadEventManual, useEventStore } from "../../services/events";
import { showError } from "../../services/toaster";
import { api } from "../../services/api/apiHandler";
import { fetchCamps } from "../../services/camps";
import { fetchCorporateExternalCatalogPackages } from "../../services/externalCatalog";
import { CorporateEventUser, Event, EventBookedUser, EventPackage, EventRegistration, EventStats } from "../../types/events";
import { ExternalCatalogRow } from "../../types/externalCatalog";
import { Camp } from "../../types/camps";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const emptyEvent: Event = {
    name: "",
    description: "",
    eventType: "AHC",
    corporateId: "",
    packages: [],
    campIds: [],
    wallpaperUrls: [],
    createCoupon: true,
    discountType: "percentage",
    discountValue: 100,
    usageLimitPerUser: 1,
    forUser: "all",
    isActive: true,
};

const getExportAmountPaid = (booking?: EventBookedUser | null) =>
    booking?.paymentStatus?.toLowerCase() === "paid" && booking.status?.toLowerCase() !== "cancelled"
        ? booking.amountPaid ?? 0
        : 0;

const getCouponOffer = (booking?: EventBookedUser | null) => {
    if (booking?.couponDiscountValue === undefined) return "";
    return booking.couponDiscountType === "percentage"
        ? `${booking.couponDiscountValue}% off`
        : booking.couponDiscountType === "fixed"
            ? `₹${booking.couponDiscountValue} off`
            : "";
};

const toApiDate = (value?: string) => value ? value.replace(/-/g, "") : undefined;
const toInputDate = (value?: string) => {
    if (!value || value.length !== 8) return "";
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
};
const formatDisplayDate = (value?: string) => value && value.length === 8 ? `${value.slice(6, 8)}/${value.slice(4, 6)}/${value.slice(0, 4)}` : value || "--";
const formatStatus = (value?: string) => value ? value.replace(/_/g, " ") : "Not Booked";
const bookingStatusOptions = ["pending", "confirmed", "in_progress", "rescheduled", "phlebo_assigned", "sample_collected", "completed", "report_delivered", "cancelled", "abandoned"];
const displayLegStatus = (leg?: { status?: string; normalizedStatus?: string }) =>
    leg?.normalizedStatus && leg.normalizedStatus !== "unknown"
        ? leg.normalizedStatus
        : leg?.status;
const formatLegStatus = (leg?: { status?: string; normalizedStatus?: string }) => {
    const status = displayLegStatus(leg);
    return status ? formatStatus(status) : "--";
};
const formatLegStatuses = (booking?: EventBookedUser | null) => {
    if (booking?.packageType !== "HYBRID") return "";
    const centreStatus = displayLegStatus(booking.legs?.centreVisit);
    const homeStatus = displayLegStatus(booking.legs?.homeCollection);
    if (!centreStatus && !homeStatus) return "";
    return `Centre: ${formatLegStatus(booking.legs?.centreVisit)} · Home: ${formatLegStatus(booking.legs?.homeCollection)}`;
};
const getStatusChipColor = (value?: string) => {
    const status = (value || "").toLowerCase();
    if (/cancel|fail|refund/.test(status)) return "error" as const;
    if (/pending/.test(status)) return "warning" as const;
    if (/complete|confirm|book|paid|scheduled|sample|report/.test(status)) return "success" as const;
    return "default" as const;
};

const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: value === index ? 2 : 0 }}>
        {value === index ? children : null}
    </Box>
);

const AddEventDialog = ({ isModalOpen, toggleModal, selectedId, hideAddButton = false, asPage = false }: any) => {
    const { onCreate, detail, onUpdate, fetchStats, fetchBookedUsers, fetchRegistrations, registerUsers, uploadRegistrations } = useEventStore();
    const { allData: corporates, fetchAll } = useCorporateStore();
    const { userData } = useAppContext();
    const isHR = isHRUser(userData);
    const [data, setData] = React.useState<Event>({ ...emptyEvent });
    const [packages, setPackages] = React.useState<ExternalCatalogRow[]>([]);
    const [camps, setCamps] = React.useState<Camp[]>([]);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [tabValue, setTabValue] = React.useState(0);
    const [registrations, setRegistrations] = React.useState<EventRegistration[]>([]);
    const [registrationBookingFilter, setRegistrationBookingFilter] = React.useState<string[]>([]);
    const [stats, setStats] = React.useState<EventStats | null>(null);
    const [bookedUsers, setBookedUsers] = React.useState<EventBookedUser[]>([]);
    const [bookingHistoryRegistration, setBookingHistoryRegistration] = React.useState<EventRegistration | null>(null);
    const [registrationFile, setRegistrationFile] = React.useState<File | null>(null);
    const [registrationMode, setRegistrationMode] = React.useState<"manual" | "file">("file");
    const [uploading, setUploading] = React.useState(false);
    const [uploadResult, setUploadResult] = React.useState<any>(null);
    const [registerModalOpen, setRegisterModalOpen] = React.useState(false);
    const [corporateUsers, setCorporateUsers] = React.useState<CorporateEventUser[]>([]);
    const [userSearch, setUserSearch] = React.useState("");
    const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);
    const [loadingCorporateUsers, setLoadingCorporateUsers] = React.useState(false);
    const [registeringUsers, setRegisteringUsers] = React.useState(false);
    const [corporateUsersError, setCorporateUsersError] = React.useState("");
    const [wallpaperFiles, setWallpaperFiles] = React.useState<File[]>([]);
    const [userManualFile, setUserManualFile] = React.useState<File | null>(null);
    const wallpaperPreviewUrls = React.useMemo(
        () => wallpaperFiles.map((file) => URL.createObjectURL(file)),
        [wallpaperFiles]
    );
    const bookingCards = React.useMemo(() => {
        const isCancelled = (booking: EventBookedUser) =>
            /cancel/i.test(booking.status || "") || /cancel/i.test(booking.paymentStatus || "");
        const isPaymentPending = (booking: EventBookedUser) =>
            /pending/i.test(booking.paymentStatus || "") || /pending_payment/i.test(booking.status || "");
        const isAttended = (booking: EventBookedUser) =>
            ["completed", "sample_collected", "report_delivered"].includes((booking.status || "").toLowerCase());
        const isBooked = (booking: EventBookedUser) =>
            !isCancelled(booking) &&
            !isPaymentPending(booking) &&
            !["completed", "sample_collected", "report_delivered"].includes((booking.status || "").toLowerCase());

        return [
            ["Registered", stats?.registeredUsers || 0],
            ["Booked", bookedUsers.filter(isBooked).length],
            ["Cancelled", bookedUsers.filter(isCancelled).length],
            ["Payment Pending", bookedUsers.filter(isPaymentPending).length],
            ["Attended", bookedUsers.filter(isAttended).length],
            ["Internal", bookedUsers.filter((booking) => booking.bookingType === "internal").length],
            ["External", bookedUsers.filter((booking) => booking.bookingType === "external").length],
            ["Dependent Bookings", bookedUsers.filter((booking) => Boolean(booking.dependentId)).length],
            ["Total Activity", bookedUsers.length],
        ];
    }, [bookedUsers, stats?.registeredUsers]);
    const filteredRegistrations = React.useMemo(() => {
        if (registrationBookingFilter.includes("not_booked")) {
            return registrations.filter((registration) => !registration.hasBooking);
        }
        if (registrationBookingFilter.length) {
            return registrations.filter((registration) =>
                registration.latestBooking?.status && registrationBookingFilter.includes(registration.latestBooking.status)
            );
        }
        return registrations;
    }, [registrationBookingFilter, registrations]);
    const selectedBookingStatuses = registrationBookingFilter.filter((status) => status !== "not_booked");
    const hasExportRows = selectedBookingStatuses.length
        ? bookedUsers.some((booking) => booking.status && selectedBookingStatuses.includes(booking.status))
        : filteredRegistrations.length > 0;

    const selectedPackageIds = data.packages.map(item => item.id);
    const registeredUserIds = React.useMemo(() => new Set(registrations.map(item => item.userId)), [registrations]);
    const filteredCorporateUsers = React.useMemo(() => {
        const term = userSearch.trim().toLowerCase();
        if (!term) return corporateUsers;
        return corporateUsers.filter(user =>
            [user.name, user.email, user.corporateEmail, user.phone, user.employeeId]
                .some(value => value?.toLowerCase().includes(term))
        );
    }, [corporateUsers, userSearch]);
    const handleChange = (key: keyof Event, value: any) => setData(prev => ({ ...prev, [key]: value }));
    const handleBookingStatusFilterChange = (value: string[]) => {
        if (value.includes("all")) {
            setRegistrationBookingFilter([]);
        } else if (value.includes("not_booked")) {
            setRegistrationBookingFilter(["not_booked"]);
        } else {
            setRegistrationBookingFilter(value.filter((status) => status !== "not_booked"));
        }
    };
    const handleClickOpen = () => toggleModal(true);
    const handleClose = () => toggleModal(false);

    React.useEffect(() => {
        return () => wallpaperPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    }, [wallpaperPreviewUrls]);

    const handleCorporateChange = async (corporateId: string) => {
        setData(prev => ({ ...prev, corporateId, packages: [], campIds: [] }));
        if (!corporateId) {
            setPackages([]);
            setCamps([]);
            return;
        }
        setPackages(await fetchCorporateExternalCatalogPackages(corporateId));
        if (data.eventType === "CAMP") {
            setCamps(await fetchCamps());
        }
    };

    const handleEventTypeChange = async (eventType: Event["eventType"]) => {
        setData(prev => ({ ...prev, eventType, campIds: eventType === "CAMP" ? prev.campIds || [] : [] }));
        if (eventType === "CAMP" && data.corporateId) {
            setCamps(await fetchCamps());
        }
    };

    const handlePackageSelection = (ids: string[]) => {
        const nextPackages: EventPackage[] = ids.map(id => {
            const existing = data.packages.find(item => item.id === id);
            const packageRecord = packages.find(item => item.externalItemId === id);
            return {
                id,
                displayName: existing?.displayName || packageRecord?.name || id,
            };
        });
        handleChange("packages", nextPackages);
    };

    const handlePackageDisplayNameChange = (id: string, displayName: string) => {
        handleChange("packages", data.packages.map(item => (
            item.id === id ? { ...item, displayName } : item
        )));
    };

    const selectRegistrationMode = (mode: "manual" | "file") => {
        if (mode === registrationMode || uploading || registeringUsers) return;
        setRegistrationMode(mode);
        setRegistrationFile(null);
        setUploadResult(null);
        setRegisterModalOpen(false);
        setSelectedUserIds([]);
        setCorporateUsersError("");
    };

    const openRegisterModal = async () => {
        if (!data.corporateId || registrationMode !== "manual") return;
        setRegisterModalOpen(true);
        setUserSearch("");
        setSelectedUserIds([]);
        setCorporateUsers([]);
        setCorporateUsersError("");
        setLoadingCorporateUsers(true);
        try {
            setCorporateUsers(await fetchCorporateEventUsers(data.corporateId));
        } catch (error) {
            setCorporateUsersError('Failed to load corporate users');
        } finally {
            setLoadingCorporateUsers(false);
        }
    };

    const submitRegistrations = async () => {
        if (!data._id || registrationMode !== "manual" || !selectedUserIds.length || registeringUsers) return;
        setRegisteringUsers(true);
        try {
            const result = await registerUsers(data._id, selectedUserIds);
            if (result) {
                await Promise.all([loadRegistrations(data._id), loadStatsAndBookings(data._id)]);
                if (result.failed.length) {
                    setSelectedUserIds(result.failed.map(item => item.userId).filter((id): id is string => Boolean(id)));
                    setCorporateUsersError(`${result.failed.length} user(s) could not be registered: ${result.failed[0].reason}`);
                } else {
                    setRegisterModalOpen(false);
                }
            }
        } finally {
            setRegisteringUsers(false);
        }
    };

    const loadRegistrations = async (eventId = data._id) => {
        if (!eventId) return;
        setRegistrations(await fetchRegistrations(eventId));
    };

    const exportRegistrations = () => {
        const bookingsToExport = selectedBookingStatuses.length
            ? bookedUsers.filter((booking) =>
                booking.status && selectedBookingStatuses.includes(booking.status)
            )
            : bookedUsers;

        downloadCsvFile(
            `event-bookings-${data.name || data._id || "export"}.csv`,
            ["Booking ID", "Name", "Email", "Phone", "Gender", "Beneficiary", "Booking Date", "Booking Time", "Package", "Type", "Provider", "Payment Status", "Amount Paid", "Coupon Code Used", "Coupon Name", "Discount Offered", "Booking Status", "Centre Status", "Home Status"],
            bookingsToExport.map((booking) => [
                booking.bookingId,
                booking.user?.name || "",
                booking.user?.email || booking.user?.corporateEmail || "",
                booking.user?.phone || "",
                booking.patientGender || booking.user?.gender || "",
                booking.dependentId ? "Dependent" : "Employee",
                formatDisplayDate(booking.bookingDate),
                booking.bookingTime || "",
                booking.packageName || "",
                booking.bookingType || "",
                booking.providerName || "",
                booking.paymentStatus || "",
                getExportAmountPaid(booking),
                booking.couponCode || "",
                booking.couponTitle || "",
                getCouponOffer(booking),
                formatStatus(booking.status),
                booking.packageType === "HYBRID" ? formatLegStatus(booking.legs?.centreVisit) : "",
                booking.packageType === "HYBRID" ? formatLegStatus(booking.legs?.homeCollection) : "",
            ])
        );
    };

    const loadStatsAndBookings = async (eventId = data._id) => {
        if (!eventId) return;
        const [eventStats, bookings] = await Promise.all([
            fetchStats(eventId),
            fetchBookedUsers(eventId),
        ]);
        setStats(eventStats);
        setBookedUsers(bookings);
    };

    const handleUpload = async () => {
        if (!data._id || registrationMode !== "file" || !registrationFile || uploading) return;
        setUploading(true);
        try {
            const result = await uploadRegistrations(data._id, registrationFile);
            setUploadResult(result);
            await Promise.all([
                loadRegistrations(data._id),
                loadStatsAndBookings(data._id),
            ]);
        } finally {
            setUploading(false);
        }
    };

    const buildPayload = () => {
        const payload: Event = {
            ...data,
            name: data.name.trim(),
            description: data.description?.trim(),
            campIds: data.eventType === "CAMP" ? data.campIds || [] : [],
            startDate: toApiDate(data.startDate),
            endDate: toApiDate(data.endDate),
            packages: data.packages.map(item => ({
                id: item.id,
                displayName: item.displayName.trim(),
            })),
        };

        if (!payload.createCoupon || payload._id) {
            delete payload.createCoupon;
            delete payload.couponTitle;
            delete payload.couponDescription;
            delete payload.discountType;
            delete payload.discountValue;
            delete payload.minimumPurchaseAmount;
            delete payload.maximumDiscountAmount;
            delete payload.usageLimitPerUser;
            delete payload.totalUsageLimit;
            delete payload.forUser;
        }

        return payload;
    };

    const uploadWallpapers = async (eventId: string, currentUrls: string[] = []) => {
        if (!wallpaperFiles.length) return currentUrls;
        const response = await uploadFile({ module: MODULES.EVENT, record_id: eventId }, wallpaperFiles);
        const files = response?.data?.data?.files || response?.data?.data || [];
        const urls = files
            .map((file: any) => file?.url || file)
            .filter((url: any): url is string => typeof url === "string" && !!url);
        return [...currentUrls, ...urls];
    };

    const onSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = buildPayload();
            if (userManualFile) {
                payload.userManualUrl = await uploadEventManual(userManualFile);
            }
            let savedEvent: Event | null = null;
            if (payload._id) {
                savedEvent = await onUpdate(payload);
            } else {
                savedEvent = await onCreate(payload);
            }
            if (!savedEvent?._id) {
                throw new Error('Could not save event');
            }
            if (savedEvent?._id && wallpaperFiles.length) {
                const wallpaperUrls = await uploadWallpapers(savedEvent._id, savedEvent.wallpaperUrls || payload.wallpaperUrls || []);
                await onUpdate({ ...savedEvent, wallpaperUrls });
            }
            handleClose();
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Could not save event');
        } finally {
            setIsSubmitting(false);
        }
    };

    const fetchDetail = async (eventId: string) => {
        const response = await detail(eventId);
        const event = response?.data;
        if (!event) return;
        setData({
            ...emptyEvent,
            ...event,
            createCoupon: Boolean(event.couponCode),
            startDate: toInputDate(event.startDate),
            endDate: toInputDate(event.endDate),
        });
        if (event.corporateId && !isHR) {
            setPackages(await fetchCorporateExternalCatalogPackages(event.corporateId));
            if (event.eventType === "CAMP") {
                setCamps(await fetchCamps());
            }
        }
        await Promise.all([
            loadRegistrations(event._id),
            loadStatsAndBookings(event._id),
        ]);
    };

    React.useEffect(() => {
        fetchAll();
    }, []);

    React.useEffect(() => {
        if (!isModalOpen && !asPage) return;
        setData({ ...emptyEvent });
        setPackages([]);
        setCamps([]);
        setTabValue(0);
        setRegistrations([]);
        setRegistrationBookingFilter([]);
        setStats(null);
        setBookedUsers([]);
        setBookingHistoryRegistration(null);
        setRegistrationFile(null);
        setRegistrationMode("file");
        setUploadResult(null);
        setRegisterModalOpen(false);
        setCorporateUsers([]);
        setSelectedUserIds([]);
        setUserSearch("");
        setWallpaperFiles([]);
        setUserManualFile(null);
        if (selectedId) {
            fetchDetail(selectedId);
        }
    }, [isModalOpen, selectedId, asPage]);

    const formContent = (
        <form onSubmit={onSubmit}>
                    <DialogTitle>{data._id ? "Edit Event" : "Add Event"}</DialogTitle>
                    <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
                        <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} aria-label="event tabs">
                            <Tab label="Event Details" />
                            <Tab label="Wallpapers" />
                            {data._id ? <Tab label="Stats & Bookings" /> : null}
                            {data._id ? <Tab label="Upload Users" /> : null}
                            {data._id ? <Tab label="Registered Users" /> : null}
                        </Tabs>
                    </Box>
                    <DialogContent dividers>
                        <TabPanel value={tabValue} index={0}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField disabled={isHR} label="Event Name" fullWidth required value={data.name} onChange={(e) => handleChange("name", e.target.value)} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required disabled={isHR}>
                                        <InputLabel>Event Type</InputLabel>
                                        <Select label="Event Type" value={data.eventType || "AHC"} onChange={(e) => handleEventTypeChange(e.target.value as Event["eventType"])}>
                                            <MenuItem value="AHC">AHC</MenuItem>
                                            <MenuItem value="PEHC">PEHC</MenuItem>
                                            <MenuItem value="CAMP">CAMP</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required disabled={isHR}>
                                        <InputLabel>Corporate</InputLabel>
                                        <Select label="Corporate" value={data.corporateId} onChange={(e) => handleCorporateChange(e.target.value)}>
                                            {corporates.map((corporate) => (
                                                <MenuItem key={corporate._id} value={corporate._id}>
                                                    {corporate.companyName || corporate.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField disabled={isHR} fullWidth label="Description" value={data.description || ""} rows={3} onChange={(e) => handleChange("description", e.target.value)} multiline />
                                </Grid>
                                {data.eventType === "CAMP" ? (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth required disabled={isHR || !data.corporateId}>
                                            <InputLabel>Camps</InputLabel>
                                            <Select
                                                multiple
                                                label="Camps"
                                                value={data.campIds || []}
                                                renderValue={(selected) => (selected as string[]).map(id => {
                                                    const camp = camps.find(item => item.campLsId === id);
                                                    return camp?.corporateName || camp?.labName || id;
                                                }).join(", ")}
                                                onChange={(e) => handleChange("campIds", e.target.value as string[])}
                                            >
                                                {camps.map((camp) => (
                                                    <MenuItem key={camp.campLsId} value={camp.campLsId}>
                                                        <Checkbox checked={(data.campIds || []).includes(camp.campLsId)} />
                                                        <ListItemText primary={camp.corporateName || camp.labName || camp.campLsId} secondary={camp.dates?.join(", ")} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ) : null}
                                <Grid item xs={12} md={6}>
                                    <TextField disabled={isHR} fullWidth label="Start Date" type="date" InputLabelProps={{ shrink: true }} value={data.startDate || ""} onChange={(e) => handleChange("startDate", e.target.value)} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField disabled={isHR} fullWidth label="End Date" type="date" InputLabelProps={{ shrink: true }} value={data.endDate || ""} onChange={(e) => handleChange("endDate", e.target.value)} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2">User Manual</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            The PDF is attached to the email sent when a user is registered for this event. Maximum 10 MB.
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={isHR}>
                                                {userManualFile ? userManualFile.name : data.userManualUrl ? "Replace PDF" : "Select PDF"}
                                                <input hidden type="file" accept="application/pdf,.pdf" onChange={(event) => {
                                                    const file = event.target.files?.[0];
                                                    event.target.value = "";
                                                    if (!file) return;
                                                    if (!file.name.toLowerCase().endsWith('.pdf') || file.size > 10 * 1024 * 1024) {
                                                        showError('Select a PDF up to 10 MB');
                                                        return;
                                                    }
                                                    setUserManualFile(file);
                                                }} />
                                            </Button>
                                            {data.userManualUrl && !userManualFile ? (
                                                <Button href={`${(api.defaults.baseURL || '').replace(/\/api\/v1\/?$/, '')}${data.userManualUrl}`} target="_blank" rel="noopener noreferrer">
                                                    View current manual
                                                </Button>
                                            ) : null}
                                            {(data.userManualUrl || userManualFile) && !isHR ? (
                                                <Button type="button" color="error" onClick={() => {
                                                    setUserManualFile(null);
                                                    handleChange('userManualUrl', null);
                                                }}>Remove</Button>
                                            ) : null}
                                        </Stack>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12}>
                                    {isHR ? (
                                        <Box>
                                            <Typography variant="subtitle2">Packages</Typography>
                                            <Typography>{data.packages.map(item => item.displayName).join(", ") || "--"}</Typography>
                                        </Box>
                                    ) : (
                                        <FormControl fullWidth required disabled={!data.corporateId}>
                                            <InputLabel>Packages</InputLabel>
                                            <Select
                                                multiple
                                                label="Packages"
                                                value={selectedPackageIds}
                                                renderValue={(selected) => (selected as string[]).map(id => data.packages.find(item => item.id === id)?.displayName || id).join(", ")}
                                                onChange={(e) => handlePackageSelection(e.target.value as string[])}
                                            >
                                                {packages.map((item) => (
                                                    <MenuItem key={`${item.integration}-${item.externalItemId}`} value={item.externalItemId}>
                                                        <Checkbox checked={selectedPackageIds.includes(item.externalItemId)} />
                                                        <ListItemText primary={item.name} secondary={item.integration} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Grid>
                                {!isHR && data.packages.map((item) => (
                                    <Grid item xs={12} md={6} key={item.id}>
                                        <TextField fullWidth label="Package Display Name" value={item.displayName} onChange={(e) => handlePackageDisplayNameChange(item.id, e.target.value)} />
                                    </Grid>
                                ))}
                                {!data._id ? (
                                    <>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={<Checkbox disabled={isHR} checked={Boolean(data.createCoupon)} onChange={(e) => handleChange("createCoupon", e.target.checked)} />}
                                                label="Create event coupon"
                                            />
                                        </Grid>
                                        {data.createCoupon ? (
                                            <>
                                                <Grid item xs={12} md={6}>
                                                    <TextField disabled={isHR} fullWidth label="Coupon Code" value={data.couponCode || ""} onChange={(e) => handleChange("couponCode", e.target.value.toUpperCase())} helperText="Leave blank to auto-generate" />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <FormControl fullWidth disabled={isHR}>
                                                        <InputLabel>Discount Type</InputLabel>
                                                        <Select label="Discount Type" value={data.discountType || "percentage"} onChange={(e) => handleChange("discountType", e.target.value)}>
                                                            <MenuItem value="percentage">Percentage</MenuItem>
                                                            <MenuItem value="fixed">Fixed</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField disabled={isHR} fullWidth required type="number" label="Discount Value" value={data.discountValue ?? ""} onChange={(e) => handleChange("discountValue", Number(e.target.value))} />
                                                </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField disabled={isHR} fullWidth type="number" label="Usage Limit Per User" value={data.usageLimitPerUser ?? ""} onChange={(e) => handleChange("usageLimitPerUser", Number(e.target.value))} />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField disabled={isHR} fullWidth type="number" label="Total Usage Limit" value={data.totalUsageLimit ?? ""} onChange={(e) => handleChange("totalUsageLimit", Number(e.target.value))} />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth disabled={isHR}>
                                                    <InputLabel>Booking User</InputLabel>
                                                    <Select label="Booking User" value={data.forUser || "all"} onChange={(e) => handleChange("forUser", e.target.value)}>
                                                        <MenuItem value="self">Self only</MenuItem>
                                                        <MenuItem value="all">Self + Dependents</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </>
                                    ) : null}
                                    </>
                                ) : (
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Coupon Code"
                                            value={data.couponCode || ""}
                                            placeholder="No coupon linked"
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{ readOnly: true }}
                                            helperText={data.couponCode ? "Code linked to this event" : "No coupon was created with this event"}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </TabPanel>
                        <TabPanel value={tabValue} index={1}>
                            <Stack spacing={2}>
                                <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={isHR}>
                                    {wallpaperFiles.length ? `${wallpaperFiles.length} wallpaper image(s) selected` : "Select Wallpaper Images"}
                                    <input
                                        hidden
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={(event) => setWallpaperFiles(Array.from(event.target.files || []))}
                                    />
                                </Button>
                                {!!data.wallpaperUrls?.length ? (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Existing Wallpapers</Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                                            {data.wallpaperUrls.map((url) => (
                                                <CustomImage key={url} src={url} style={{ width: 160, height: 90, objectFit: "cover", borderRadius: 4 }} />
                                            ))}
                                        </Box>
                                    </Box>
                                ) : null}
                                {!!wallpaperPreviewUrls.length ? (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Selected Wallpapers</Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                                            {wallpaperPreviewUrls.map((url) => (
                                                <img key={url} src={url} alt="Selected wallpaper preview" style={{ width: 160, height: 90, objectFit: "cover", borderRadius: 4 }} />
                                            ))}
                                        </Box>
                                    </Box>
                                ) : null}
                            </Stack>
                        </TabPanel>
                        {data._id ? (
                            <>
                                <TabPanel value={tabValue} index={2}>
                                    <Stack spacing={2}>
                                        <Grid container spacing={2}>
                                            {bookingCards.map(([label, value]) => (
                                                <Grid item xs={6} md={3} key={String(label)}>
                                                    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                        <Typography variant="h5" color="primary">{value}</Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={1}>
                                            <Typography variant="subtitle1">Registered Users</Typography>
                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                                    <InputLabel>Latest Booking Status</InputLabel>
                                                    <Select
                                                        multiple
                                                        label="Latest Booking Status"
                                                        value={registrationBookingFilter}
                                                        onChange={(event) => {
                                                            const value = event.target.value;
                                                            handleBookingStatusFilterChange(
                                                                typeof value === "string" ? value.split(",") : value
                                                            );
                                                        }}
                                                        renderValue={(selected) => selected.length
                                                            ? selected.map(formatStatus).join(", ")
                                                            : "All"}
                                                    >
                                                        <MenuItem value="all">
                                                            <Checkbox checked={!registrationBookingFilter.length} />
                                                            <ListItemText primary="All" />
                                                        </MenuItem>
                                                        <MenuItem value="not_booked">
                                                            <Checkbox checked={registrationBookingFilter.includes("not_booked")} />
                                                            <ListItemText primary="Not Booked" />
                                                        </MenuItem>
                                                        {bookingStatusOptions.map((status) => (
                                                            <MenuItem key={status} value={status}>
                                                                <Checkbox checked={registrationBookingFilter.includes(status)} />
                                                                <ListItemText primary={formatStatus(status)} />
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<FileDownloadOutlinedIcon />}
                                                    onClick={exportRegistrations}
                                                    disabled={!hasExportRows}
                                                >
                                                    {selectedBookingStatuses.length
                                                        ? "Export Selected Bookings"
                                                        : "Export Registered Users"}
                                                </Button>
                                            </Stack>
                                        </Stack>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>User</TableCell>
                                                    <TableCell>Booking Status</TableCell>
                                                    <TableCell>Last Booking Date</TableCell>
                                                    <TableCell>Last Booking Time</TableCell>
                                                    <TableCell>Last Package</TableCell>
                                                    <TableCell>Last Type</TableCell>
                                                    <TableCell>Last Payment Status</TableCell>
                                                    <TableCell>Latest Booking Status</TableCell>
                                                    <TableCell>History</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {filteredRegistrations.map((registration) => (
                                                    <TableRow key={registration._id}>
                                                        <TableCell>
                                                            <Typography variant="body2">{registration.user?.name || "--"}</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {registration.user?.email || registration.user?.corporateEmail || registration.user?.phone || registration.userId}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                size="small"
                                                                color={getStatusChipColor(registration.latestBooking?.status)}
                                                                label={formatStatus(registration.latestBooking?.status)}
                                                            />
                                                            {formatLegStatuses(registration.latestBooking) ? (
                                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                    {formatLegStatuses(registration.latestBooking)}
                                                                </Typography>
                                                            ) : null}
                                                        </TableCell>
                                                        <TableCell>{formatDisplayDate(registration.latestBooking?.bookingDate)}</TableCell>
                                                        <TableCell>{registration.latestBooking?.bookingTime || "--"}</TableCell>
                                                        <TableCell>{registration.latestBooking?.packageName || "--"}</TableCell>
                                                        <TableCell>
                                                            {registration.latestBooking?.bookingType ? (
                                                                <Chip size="small" label={registration.latestBooking.bookingType} />
                                                            ) : "--"}
                                                        </TableCell>
                                                        <TableCell>
                                                            {registration.latestBooking?.paymentStatus ? (
                                                                <Chip
                                                                    size="small"
                                                                    color={getStatusChipColor(registration.latestBooking.paymentStatus)}
                                                                    label={formatStatus(registration.latestBooking.paymentStatus)}
                                                                />
                                                            ) : "--"}
                                                        </TableCell>
                                                        <TableCell>
                                                            {registration.latestBooking?.status ? (
                                                                <>
                                                                    <Chip
                                                                        size="small"
                                                                        color={getStatusChipColor(registration.latestBooking.status)}
                                                                        label={formatStatus(registration.latestBooking.status)}
                                                                    />
                                                                    {formatLegStatuses(registration.latestBooking) ? (
                                                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                            {formatLegStatuses(registration.latestBooking)}
                                                                        </Typography>
                                                                    ) : null}
                                                                </>
                                                            ) : "--"}
                                                        </TableCell>
                                                        <TableCell>
                                                            {registration.hasBooking ? (
                                                                <IconButton size="small" onClick={() => setBookingHistoryRegistration(registration)}>
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            ) : "--"}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {!filteredRegistrations.length ? (
                                                    <TableRow>
                                                        <TableCell colSpan={9}>No registered users found.</TableCell>
                                                    </TableRow>
                                                ) : null}
                                            </TableBody>
                                        </Table>
                                    </Stack>
                                </TabPanel>
                                <TabPanel value={tabValue} index={3}>
                                    <Stack spacing={2}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Button
                                                type="button"
                                                variant={registrationMode === "manual" ? "contained" : "outlined"}
                                                aria-pressed={registrationMode === "manual"}
                                                disabled={uploading || registeringUsers}
                                                onClick={() => selectRegistrationMode("manual")}
                                            >
                                                Select Users
                                            </Button>
                                            <Typography variant="body2" color="text.secondary">OR</Typography>
                                            <Button
                                                type="button"
                                                variant={registrationMode === "file" ? "contained" : "outlined"}
                                                aria-pressed={registrationMode === "file"}
                                                disabled={uploading || registeringUsers}
                                                onClick={() => selectRegistrationMode("file")}
                                            >
                                                Upload File
                                            </Button>
                                        </Stack>
                                        {registrationMode === "manual" ? (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Search and select users from this corporate.
                                                </Typography>
                                                <Button type="button" variant="outlined" onClick={openRegisterModal}>
                                                    Select Users to Register
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Stack spacing={2}>
                                                <Button
                                                    component="a"
                                                    href={`${process.env.PUBLIC_URL || ""}/sample-event-registration-upload-template.csv`}
                                                    download="sample-event-registration-upload-template.csv"
                                                    variant="outlined"
                                                    startIcon={<FileDownloadOutlinedIcon />}
                                                >
                                                    Download Sample File
                                                </Button>
                                                <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
                                                    {registrationFile ? registrationFile.name : "Select CSV, XLS, or XLSX"}
                                                    <input
                                                        hidden
                                                        type="file"
                                                        accept=".csv,.xls,.xlsx"
                                                        onChange={(event) => {
                                                            setRegistrationFile(event.target.files?.[0] || null);
                                                            setUploadResult(null);
                                                        }}
                                                    />
                                                </Button>
                                                <Typography variant="body2" color="text.secondary">
                                                    Supported headers: userId, email, corporateEmail, phone, employeeId.
                                                </Typography>
                                                <Button type="button" variant="contained" onClick={handleUpload} disabled={!registrationFile || uploading}>
                                                    {uploading ? "Uploading..." : "Upload"}
                                                </Button>
                                                {uploadResult ? (
                                                    <Typography variant="body2">
                                                        Registered: {uploadResult.registered?.length || 0} | Skipped: {uploadResult.skipped?.length || 0} | Failed: {uploadResult.failed?.length || 0}
                                                    </Typography>
                                                ) : null}
                                                {uploadResult?.failed?.length ? (
                                                    <Typography variant="body2" color="error">
                                                        First failed row {uploadResult.failed[0].rowNumber || "--"}: {uploadResult.failed[0].reason}
                                                    </Typography>
                                                ) : null}
                                            </Stack>
                                        )}
                                    </Stack>
                                </TabPanel>
                                <TabPanel value={tabValue} index={4}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Phone</TableCell>
                                                <TableCell>Gender</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {registrations.map((registration) => (
                                                <TableRow key={registration._id}>
                                                    <TableCell>{registration.user?.name || "--"}</TableCell>
                                                    <TableCell>{registration.user?.email || registration.user?.corporateEmail || "--"}</TableCell>
                                                    <TableCell>{registration.user?.phone || "--"}</TableCell>
                                                    <TableCell>{registration.user?.gender || "--"}</TableCell>
                                                </TableRow>
                                            ))}
                                            {!registrations.length ? (
                                                <TableRow>
                                                    <TableCell colSpan={4}>No registered users found.</TableCell>
                                                </TableRow>
                                            ) : null}
                                        </TableBody>
                                    </Table>
                                </TabPanel>
                            </>
                        ) : null}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        {tabValue === 3 && data._id ? null : isHR ? (
                            <Button type="button" variant="contained" onClick={handleClose}>Close</Button>
                        ) : (
                            <Button type="submit" variant="contained" disabled={isSubmitting}>Submit</Button>
                        )}
                    </DialogActions>
                    <Dialog
                        open={registerModalOpen}
                        onClose={() => !registeringUsers && setRegisterModalOpen(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>Register Corporate Users</DialogTitle>
                        <DialogContent dividers>
                            <Stack spacing={2}>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    label="Search users"
                                    placeholder="Name, email, phone, or employee ID"
                                    value={userSearch}
                                    onChange={(event) => setUserSearch(event.target.value)}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {selectedUserIds.length} selected · {filteredCorporateUsers.length} shown
                                </Typography>
                                {corporateUsersError ? <Typography color="error">{corporateUsersError}</Typography> : null}
                                {loadingCorporateUsers ? <Typography>Loading users...</Typography> : (
                                    <List sx={{ maxHeight: 360, overflowY: "auto" }}>
                                        {filteredCorporateUsers.map((user) => {
                                            const alreadyRegistered = registeredUserIds.has(user._id);
                                            const checked = selectedUserIds.includes(user._id);
                                            return (
                                                <ListItemButton
                                                    key={user._id}
                                                    disabled={alreadyRegistered}
                                                    onClick={() => setSelectedUserIds(previous =>
                                                        checked ? previous.filter(id => id !== user._id) : [...previous, user._id]
                                                    )}
                                                >
                                                    <Checkbox checked={checked || alreadyRegistered} tabIndex={-1} disableRipple readOnly />
                                                    <ListItemText
                                                        primary={user.name || user.email || user.corporateEmail || user._id}
                                                        secondary={[user.email || user.corporateEmail, user.phone, alreadyRegistered ? "Already registered" : user.employeeId]
                                                            .filter(Boolean).join(" · ")}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                        {!filteredCorporateUsers.length && !corporateUsersError ? (
                                            <Typography sx={{ px: 2, py: 1 }}>No users found.</Typography>
                                        ) : null}
                                    </List>
                                )}
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button type="button" onClick={() => setRegisterModalOpen(false)} disabled={registeringUsers}>Cancel</Button>
                            <Button type="button" variant="contained" onClick={submitRegistrations} disabled={!selectedUserIds.length || registeringUsers}>
                                {registeringUsers ? "Registering..." : `Register ${selectedUserIds.length} user${selectedUserIds.length === 1 ? "" : "s"}`}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        open={Boolean(bookingHistoryRegistration)}
                        onClose={() => setBookingHistoryRegistration(null)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>
                            Booking History - {bookingHistoryRegistration?.user?.name || bookingHistoryRegistration?.user?.email || bookingHistoryRegistration?.userId}
                        </DialogTitle>
                        <DialogContent dividers>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Booking Date</TableCell>
                                        <TableCell>Booking Time</TableCell>
                                        <TableCell>Package</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Beneficiary</TableCell>
                                        <TableCell>Payment Status</TableCell>
                                        <TableCell>Booking Status</TableCell>
                                        <TableCell>Provider</TableCell>
                                        <TableCell>Booking ID</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(bookingHistoryRegistration?.bookings || []).map((booking) => (
                                        <TableRow key={`${booking.bookingType}-${booking.bookingId}`}>
                                            <TableCell>{formatDisplayDate(booking.bookingDate)}</TableCell>
                                            <TableCell>{booking.bookingTime || "--"}</TableCell>
                                            <TableCell>{booking.packageName || "--"}</TableCell>
                                            <TableCell><Chip size="small" label={booking.bookingType} /></TableCell>
                                            <TableCell>{booking.dependentId ? "Dependent" : "Employee"}</TableCell>
                                            <TableCell>{booking.paymentStatus || "--"}</TableCell>
                                            <TableCell>
                                                {booking.status ? formatStatus(booking.status) : "--"}
                                                {formatLegStatuses(booking) ? (
                                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                                        {formatLegStatuses(booking)}
                                                    </Typography>
                                                ) : null}
                                            </TableCell>
                                            <TableCell>{booking.providerName || "--"}</TableCell>
                                            <TableCell>{booking.bookingId}</TableCell>
                                        </TableRow>
                                    ))}
                                    {!bookingHistoryRegistration?.bookings?.length ? (
                                        <TableRow>
                                            <TableCell colSpan={9}>No booking history found.</TableCell>
                                        </TableRow>
                                    ) : null}
                                </TableBody>
                            </Table>
                        </DialogContent>
                        <DialogActions>
                            <Button type="button" onClick={() => setBookingHistoryRegistration(null)}>Close</Button>
                        </DialogActions>
                    </Dialog>
        </form>
    );

    if (asPage) {
        return (
            <Box sx={{ bgcolor: "background.paper", borderRadius: 2, overflow: "hidden" }}>
                {formContent}
            </Box>
        );
    }

    return (
        <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <SearchInput />
                {!hideAddButton ? (
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleClickOpen}>
                        Add Event
                    </Button>
                ) : null}
            </Stack>

            <Dialog open={Boolean(isModalOpen)} onClose={handleClose} TransitionComponent={Transition} maxWidth="md" fullWidth>
                {formContent}
            </Dialog>
        </>
    );
};

export default AddEventDialog;
